import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Sparkles, X, Loader2 } from "lucide-react";
import { BANK_INFO } from "@/data/services";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * VidenteFarewell — full-screen modal that auto-plays a personalized
 * farewell audio (Claude + TTS) when the user's Vidente Virtual time
 * runs out, then offers a clean WhatsApp CTA. The WhatsApp number is
 * NOT embedded here — we reuse the site-wide BANK_INFO.whatsappLink
 * so a single source of truth controls the contact channel.
 */
export const VidenteFarewell = ({ open, onClose }) => {
  const { authHeader } = useAuth();
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null); // { text, audio_b64 }
  const audioRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setData(null);
      try { audioRef.current?.pause?.(); } catch { /* noop */ }
      return;
    }
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const r = await axios.post(`${API}/vidente/farewell`, {}, { headers: authHeader() });
        if (!cancelled) setData(r.data);
      } catch {
        // Don't block the modal — even without audio the user can still
        // see the message and the WhatsApp CTA.
        if (!cancelled) setData({
          text: "El tiempo de nuestra consulta llegó a su fin. Si necesitas continuar, escríbeme por WhatsApp y seguimos juntos.",
          audio_b64: "",
        });
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, authHeader]);

  useEffect(() => {
    if (!data?.audio_b64 || !audioRef.current) return;
    // Try to auto-play; ignore browser policy errors silently.
    audioRef.current.play?.().catch(() => { /* user can press play */ });
  }, [data?.audio_b64]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="vidente-farewell"
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative w-full max-w-lg rounded-2xl border border-[#d4af37]/40 bg-gradient-to-b from-[#1a0d2e] via-[#0b0518] to-black p-6 shadow-2xl"
            initial={{ y: 20, scale: 0.96 }} animate={{ y: 0, scale: 1 }} exit={{ y: 20, scale: 0.96 }}
          >
            <button
              data-testid="vidente-farewell-close"
              onClick={onClose}
              className="absolute top-3 right-3 text-neutral-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-14 h-14 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-[#d4af37]" />
              </div>
              <h2 className="font-serif text-xl text-[#d4af37]">El tiempo de la consulta concluyó</h2>

              {busy ? (
                <div className="flex items-center gap-2 text-neutral-400 text-sm py-4">
                  <Loader2 className="w-4 h-4 animate-spin" /> Preparando despedida...
                </div>
              ) : (
                <>
                  <p
                    data-testid="vidente-farewell-text"
                    className="text-sm text-neutral-200 leading-relaxed whitespace-pre-line"
                  >
                    {data?.text}
                  </p>

                  {data?.audio_b64 && (
                    <audio
                      ref={audioRef}
                      data-testid="vidente-farewell-audio"
                      controls
                      preload="auto"
                      className="w-full mt-2"
                      src={`data:audio/mpeg;base64,${data.audio_b64}`}
                    />
                  )}
                </>
              )}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-2 items-center justify-center">
              <a
                href={BANK_INFO.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="vidente-farewell-whatsapp"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500/90 hover:bg-emerald-500 text-black font-semibold text-sm shadow-lg shadow-emerald-500/30"
              >
                <Phone className="w-4 h-4" />
                Continuar por WhatsApp
              </a>
              <button
                onClick={onClose}
                className="text-xs text-neutral-400 hover:text-white px-3 py-2"
              >
                Cerrar
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
